import { useState } from 'react';
import type { ReactNode } from 'react';
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaChevronDown, FaChevronRight, FaRobot } from 'react-icons/fa';
import { AiRecommendation } from './types';
import type { AiAnalysisResponse, AiAnalysisResultItem } from './types';
import useAiAnalysisPolling from './useAiAnalysisPolling';
import { formatConfidence, formatPdefault, formatProcessingTime, getPdefaultColorClass } from './utils/aiAnalysisUtils';
import { AiResultBody, CloseButton, RecommendationBadge } from './components/AiResultDetails';
import ActionButton from './components/ActionButton';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & VARIABLES
// ═══════════════════════════════════════════════════════════════

const STATUS_STYLES: Record<string, string> = {
    COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    PROCESSING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    FAILED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    NOT_STARTED: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300',
};

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface AiAnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    batchId: string;
    justDispatched?: boolean;
    /** When provided, the modal uses this data instead of its own polling (single source of truth). */
    data?: AiAnalysisResponse | null;
    loading?: boolean;
    error?: string | null;
}

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const getProgressPercent = (progress: AiAnalysisResponse['progress']): number => {
    if (!progress.total) return 0;
    return Math.round((progress.completed / progress.total) * 100);
};

const countByRecommendation = (results: AiAnalysisResultItem[], recommendation: AiRecommendation): number =>
    results.filter(r => r.recommendation === recommendation).length;

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

/** Centered empty/loading state with an icon, message and optional hint. */
function EmptyState({ icon, message, hint }: { icon: ReactNode; message: string; hint?: string }) {
    return (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {icon}
            <p className={hint ? 'font-medium text-gray-700 dark:text-gray-300' : undefined}>{message}</p>
            {hint && <p className="text-sm mt-1">{hint}</p>}
        </div>
    );
}

/** A compact metric with a tiny label above a value (used in the collapsed row). */
function CompactMetric({ label, value, valueClass, width, title }: { label: string; value: ReactNode; valueClass: string; width: string; title: string }) {
    return (
        <span className={`shrink-0 text-right ${width}`} title={title}>
            <span className="block text-[10px] text-gray-400 dark:text-gray-500 leading-none">{label}</span>
            <span className={valueClass}>{value}</span>
        </span>
    );
}

/** Progress bar with completed/total and pending/failed counters. */
function ProgressBar({ progress }: { progress: AiAnalysisResponse['progress'] }) {
    const percent = getProgressPercent(progress);
    const { total, completed, pending, failed } = progress;

    return (
        <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span>{completed}/{total} concluídos</span>
                <span>{percent}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 dark:bg-green-400 transition-all duration-500 rounded-full" style={{ width: `${percent}%` }} />
            </div>
            {(pending > 0 || failed > 0) && (
                <div className="flex gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {pending > 0 && <span>⏳ {pending} pendentes</span>}
                    {failed > 0 && <span className="text-red-500 dark:text-red-400">❌ {failed} com erro</span>}
                </div>
            )}
        </div>
    );
}

/** The three recommendation counters shown in the results header. */
function RecommendationCounters({ results }: { results: AiAnalysisResultItem[] }) {
    return (
        <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
                <FaCheckCircle className="text-green-500 w-3 h-3" />
                {countByRecommendation(results, AiRecommendation.APPROVE)}
            </span>
            <span className="flex items-center gap-1">
                <FaTimesCircle className="text-red-500 w-3 h-3" />
                {countByRecommendation(results, AiRecommendation.REJECT)}
            </span>
            <span className="flex items-center gap-1">
                <FaExclamationTriangle className="text-yellow-500 w-3 h-3" />
                {countByRecommendation(results, AiRecommendation.REVIEW)}
            </span>
        </div>
    );
}

/** An expandable row for a single drawee result. */
function ResultRow({ item }: { item: AiAnalysisResultItem }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border border-gray-200 dark:border-slate-600 rounded-lg mb-2 overflow-hidden">
            <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left"
                onClick={() => setExpanded(e => !e)}
            >
                {expanded ? <FaChevronDown className="text-gray-400 shrink-0" /> : <FaChevronRight className="text-gray-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-gray-100 truncate block">{item.entityName}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{item.document}</span>
                </div>
                <RecommendationBadge recommendation={item.recommendation} />
                <CompactMetric
                    label="PDefault 1a"
                    title="Probabilidade de default em 1 ano (estimada pela IA)"
                    width="w-20"
                    valueClass={`text-sm font-medium ${getPdefaultColorClass(item.pdefault1y)}`}
                    value={formatPdefault(item.pdefault1y)}
                />
                <CompactMetric
                    label="Confiança"
                    title="Confiança da IA na recomendação"
                    width="w-14"
                    valueClass="text-sm text-gray-500 dark:text-gray-400"
                    value={formatConfidence(item.confidence)}
                />
            </button>

            {expanded && (
                <div className="px-4 pb-4 pt-3 border-t border-gray-100 dark:border-slate-700">
                    <AiResultBody
                        data={item}
                        footer={!item.errorMessage && (
                            <div className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                                Processado em {formatProcessingTime(item.processingTimeMs)}
                            </div>
                        )}
                    />
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function AiAnalysisModal({ isOpen, onClose, batchId, justDispatched, data: dataProp, loading: loadingProp, error: errorProp }: AiAnalysisModalProps) {
    const useOwnPolling = dataProp === undefined;
    const own = useAiAnalysisPolling({ batchId, enabled: isOpen && useOwnPolling, keepPollingOnNotStarted: justDispatched });
    const data = useOwnPolling ? own.data : dataProp;
    const loading = useOwnPolling ? own.loading : (loadingProp ?? false);
    const error = useOwnPolling ? own.error : (errorProp ?? null);

    if (!isOpen) return null;

    const statusStyle = data ? (STATUS_STYLES[data.status] || STATUS_STYLES.NOT_STARTED) : '';
    // After dispatch the backend may still report NOT_STARTED until the first
    // result lands — treat it as "in progress" so the user isn't told it's empty.
    const isAwaitingFirstResult = data?.status === 'NOT_STARTED' && justDispatched;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-600 px-6 py-4 z-10 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <FaRobot className="text-purple-500 dark:text-purple-400 w-5 h-5" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Análise de IA</h2>
                        {data && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusStyle}`}>
                                {data.statusFormatted}
                            </span>
                        )}
                        {loading && data?.status === 'PROCESSING' && (
                            <FaSpinner className="animate-spin text-gray-400 w-4 h-4" />
                        )}
                    </div>
                    <CloseButton onClose={onClose} />
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 rounded text-sm text-red-700 dark:text-red-300">
                            {error}
                        </div>
                    )}

                    {!data && loading && (
                        <div className="flex items-center justify-center py-12">
                            <FaSpinner className="animate-spin text-gray-400 w-6 h-6" />
                            <span className="ml-3 text-gray-500 dark:text-gray-400">Carregando resultados...</span>
                        </div>
                    )}

                    {data && data.status === 'NOT_STARTED' && (
                        isAwaitingFirstResult ? (
                            <EmptyState
                                icon={<FaSpinner className="animate-spin w-10 h-10 mx-auto mb-3 text-purple-400" />}
                                message="Análise em andamento"
                                hint="O agente está processando os sacados. Os resultados aparecem aqui automaticamente."
                            />
                        ) : (
                            <EmptyState
                                icon={<FaRobot className="w-12 h-12 mx-auto mb-3 opacity-30" />}
                                message="Nenhuma análise de IA disponível para este lote."
                                hint='Clique em "Análise IA" para iniciar.'
                            />
                        )
                    )}

                    {data && data.status !== 'NOT_STARTED' && (
                        <>
                            <ProgressBar progress={data.progress} />

                            {data.results.length === 0 && data.status === 'PROCESSING' && (
                                <EmptyState
                                    icon={<FaSpinner className="animate-spin w-8 h-8 mx-auto mb-3 opacity-50" />}
                                    message="Aguardando resultados do agente..."
                                />
                            )}

                            {data.results.length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Resultados ({data.results.length})
                                        </h3>
                                        <RecommendationCounters results={data.results} />
                                    </div>
                                    {data.results.map(item => (
                                        <ResultRow key={item.analysisItemId} item={item} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-slate-600 px-6 py-3 flex justify-end shrink-0">
                    <ActionButton variant="cancel" size="sm" onClick={onClose}>
                        Fechar
                    </ActionButton>
                </div>
            </div>
        </div>
    );
}
